import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, MapPin, Star } from "lucide-react";
import { scrollToId } from "../data";
import { useLang } from "../i18n";

export const Hero = () => {
  const { s } = useLang();
  const LINES = [s.hero.line1, s.hero.line2, s.hero.line3];
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 260]);
  const wmY = useTransform(scrollY, [0, 900], [0, -140]);
  const featherY = useTransform(scrollY, [0, 900], [0, 150]);

  return (
    <section id="top" className="relative min-h-screen overflow-hidden bg-night" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img
          src="/images/hero.jpg"
          alt="Vrindavan temple at sunset near Shri Jamuna Dham guest house"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-night/80 via-night/35 to-night" />
      </motion.div>

      <motion.span
        style={{ y: wmY }}
        aria-hidden="true"
        className="pointer-events-none select-none absolute -right-6 bottom-10 font-hindi text-[26vw] leading-none text-cream/[0.05]"
      >
        राधे
      </motion.span>

      <motion.div
        style={{ y: featherY }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 0.38, scale: 1 }}
        transition={{ duration: 1.6, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none select-none absolute right-[2%] top-[12%] z-[5]"
        aria-hidden="true"
      >
        <motion.img
          src="/images/peacock-feather.png"
          alt=""
          animate={{ rotate: [-3, 3, -3] }}
          transition={{ repeat: Infinity, duration: 9, ease: "easeInOut" }}
          className="w-52 sm:w-72 lg:w-[26rem] drop-shadow-2xl"
          data-testid="hero-peacock-feather"
        />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 min-h-screen flex flex-col justify-center pt-24 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-12 bg-gold" />
          <p className="font-hindi text-gold text-lg sm:text-xl tracking-wide" data-testid="hero-hindi-overline">
            श्री जमुना धाम
          </p>
          <span className="h-px w-12 bg-gold" />
        </motion.div>

        <h1 className="font-display font-black text-cream text-5xl sm:text-7xl lg:text-8xl leading-[0.98] tracking-tight" data-testid="hero-heading">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "italic text-saffron" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.35 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-8 max-w-xl text-cream/80 text-base sm:text-lg leading-relaxed"
          data-testid="hero-subtext"
        >
          {s.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("#booking")}
            data-testid="hero-book-stay-button"
            className="rounded-full bg-saffron px-8 py-4 text-sm font-semibold tracking-wide text-night transition-colors duration-300 hover:bg-gold"
          >
            {s.hero.cta1}
          </button>
          <button
            onClick={() => scrollToId("#rooms")}
            data-testid="hero-explore-rooms-button"
            className="rounded-full border border-cream/40 px-8 py-4 text-sm font-semibold tracking-wide text-cream transition-colors duration-300 hover:border-saffron hover:text-saffron"
          >
            {s.hero.cta2}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-cream/70 text-sm"
          data-testid="hero-highlights"
        >
          {s.hero.highlights.map((h, i) => (
            <span key={i} className="flex items-center gap-2">
              {i === 0 ? (
                <MapPin size={16} className="text-saffron" />
              ) : (
                <Star size={16} className="text-saffron" />
              )}
              {h}
            </span>
          ))}
        </motion.div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        onClick={() => scrollToId("#about")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-cream/60 hover:text-saffron transition-colors duration-300"
        data-testid="hero-scroll-down-button"
        aria-label="Scroll down"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="block">
          <ChevronDown size={28} />
        </motion.span>
      </motion.button>
    </section>
  );
};
